import { inject, Injectable } from '@angular/core';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { SUBSCRIPTION_CONSTANTS } from '@constants/subscription.constants';
import { SubscriptionModel } from '@db-models/subscribe.model';
import { SubscriptionService } from '@feature-services/subscription.service';
import { AlertToastrService } from '@util-services/alert-toastr.service';
import { CryptoService } from '@util-services/crypto.service';
import { LocalStorageService } from '@util-services/local-storage.service';
import { LoggerService } from '@util-services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class FeatureService {

  private alertToasterService = inject(AlertToastrService);
  private cryptoService = inject(CryptoService);
  private localStorageService = inject(LocalStorageService);
  private subscriptionService = inject(SubscriptionService);

  // Lock / upsell message per required subscription tier, keyed by the v2
  // subscription IDs (202-205) that features.required_subscription_id now stores
  // (remapped from the retired legacy v1 IDs by the cakephp migration). Basic +
  // Starter tiers share requires_starter (no dedicated Basic copy); Team uses
  // requires_team; Enterprise as-is.
  private readonly featureUpsellMessageByPlanId: Record<number, string> = {
    [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_BASIC_TYPE.id]: 'your_subscription_component.requires_starter',
    [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_STARTER_TYPE.id]: 'your_subscription_component.requires_starter',
    [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_TEAM_TYPE.id]: 'your_subscription_component.requires_team',
    [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_ENTERPRISE_TYPE.id]: 'your_subscription_component.requires_enterprise',
  };

  protected featuresList: { [key: string]: boolean | number } = {};

  /**
   * Feature names the partner holds through a purchased add-on rather than
   * through their subscription tier. The backend stamps `is_addon` on every
   * feature it merges in from addons_features; without keeping it, the
   * dashboard cannot tell "included in your plan" from "you bought this",
   * and keeps showing an upgrade badge for a feature the partner owns.
   */
  protected addonGrantedFeatures = new Set<string>();
  protected masterFeaturesList: { requiredSubscriptionPlan: number }[] = [];

  initializeFeature() {
    const subscriptionModel = this.getFeatureFromLocalStorage();
    this.loadSubscriptionFeatures(subscriptionModel);
    this.loadFeaturesMasterList();
  }

  setFeatureInLocalStorage(featureSubscription: SubscriptionModel): void {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify(featureSubscription)
    );
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.FEATURE_KEY, encryptedString);
    LoggerService.log('[Subscription] Features stored in local storage');
  }

  private getFeatureFromLocalStorage(): SubscriptionModel | null {
    const encrypted = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.FEATURE_KEY) as string;
    if (encrypted) {
      const decrypted = this.cryptoService.decryptValue(encrypted) as string;
      return Object.assign({}, JSON.parse(decrypted)) as SubscriptionModel;
    }
    return null;
  }

  loadSubscriptionFeatures(subscription: SubscriptionModel): void {
    this.featuresList = {};
    this.addonGrantedFeatures.clear();
    if (subscription?.subscription_type?.features) {
      subscription.subscription_type.features.forEach(feature => {
        if (feature.type === 'checkbox') {
          this.featuresList[feature.name] = true;
        } else {
          this.featuresList[feature.name] = Number(feature._joinData.value);
        }

        // Add-on-granted features are merged into this same list by the
        // backend and flagged there. They carry no `type`, so they always
        // take the numeric branch above with a hardcoded value of '1'.
        if (feature.is_addon) {
          this.addonGrantedFeatures.add(feature.name);
        }
      });
    }
  }

  /**
   * True when the partner holds this feature through a purchased add-on
   * rather than their subscription tier. Callers use it to suppress
   * tier-upgrade messaging for something the partner already owns.
   */
  isFeatureGrantedByAddon(featureName: string): boolean {
    return this.addonGrantedFeatures.has(featureName);
  }

  loadFeaturesMasterList(): void {
    const masterFeaturesList = this.subscriptionService.getFeaturesListFromLocalStorage();
    masterFeaturesList?.forEach(feature => {
      this.masterFeaturesList[feature.name] = {
        requiredSubscriptionPlan: Number(feature.required_subscription_id)
      };
    });
  }

  hasFeature(featureName: string): boolean | number {
    if (featureName) {
      return this.featuresList[featureName] ? this.featuresList[featureName] : false;
    }
    return false;
  }

  /**
   * Returns every feature flag name currently active on the partner — i.e.
   * every key in `featuresList` whose value is truthy. Numeric quotas
   * (e.g. `included_smart_widgets = 5`) ARE considered active because the
   * partner has a non-zero allotment of the gated capability.
   *
   * Consumers (e.g. the embedded pricing table) get the raw flag list and
   * match it against their own addon → featureFlag mapping, so this method
   * stays embedder-agnostic.
   */
  getActiveFeatures(): string[] {
    return Object.keys(this.featuresList).filter(flag => Boolean(this.featuresList[flag]));
  }

  featureRequiredSubscriptionPlan(featureName: string): number | null {
    if (featureName) {
      return this.masterFeaturesList[featureName] ? this.masterFeaturesList[featureName]['requiredSubscriptionPlan'] : null;
    }
    return null;
  }

  /** Plan-tier message key for a feature, or null for an add-on / unknown / whitelabel (100) tier. */
  requiredPlanMessageKey(featureName: string): string | null {
    return this.featureUpsellMessageByPlanId[this.featureRequiredSubscriptionPlan(featureName)] ?? null;
  }

  featureNotAvailable(featureName = null, showToast = true): string | void {
    let message = 'your_subscription_component.feature_not_available_for_subscribed_plan';
    if (featureName) {
      // Unknown / null / whitelabel (100) fall back to the generic message.
      message = this.requiredPlanMessageKey(featureName) ?? message;
    }

    if (showToast) {
      this.alertToasterService.showError(message);
      return;
    }
    return message;
  }
}
